import { useState, useEffect } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { ChevronLeft, ChevronRight, X } from "lucide-react"

interface ImageGalleryModalProps {
    images: string[]
    initialIndex?: number
    isOpen: boolean
    onClose: () => void
}

export function ImageGalleryModal({ images, initialIndex = 0, isOpen, onClose }: ImageGalleryModalProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex)

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex)
        }
    }, [isOpen, initialIndex])

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % images.length)
    }

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
    }

    if (!images || images.length === 0) return null

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[95vw] sm:max-w-[85vw] h-[95vh] sm:h-[85vh] p-0 bg-black/95 border-none shadow-2xl flex flex-col overflow-hidden [&>button]:hidden">
                <div className="absolute top-4 right-4 z-50">
                    <button
                        onClick={onClose}
                        className="rounded-full bg-black/50 p-2 text-white hover:bg-black/80 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="flex-1 relative flex items-center justify-center p-4 min-h-0">
                    {images.length > 1 && (
                        <button
                            onClick={handlePrev}
                            className="absolute left-4 z-10 rounded-full bg-black/50 p-3 text-white hover:bg-black/80 transition-colors backdrop-blur-md"
                        >
                            <ChevronLeft className="h-8 w-8" />
                        </button>
                    )}

                    <img
                        src={images[currentIndex]}
                        alt={`Gallery Image ${currentIndex + 1}`}
                        className="max-w-full max-h-full object-contain select-none"
                    />

                    {images.length > 1 && (
                        <button
                            onClick={handleNext}
                            className="absolute right-4 z-10 rounded-full bg-black/50 p-3 text-white hover:bg-black/80 transition-colors backdrop-blur-md"
                        >
                            <ChevronRight className="h-8 w-8" />
                        </button>
                    )}
                </div>

                {images.length > 1 && (
                    <div className="h-24 bg-black/60 backdrop-blur-md p-4 flex gap-3 overflow-x-auto justify-center shrink-0">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`h-full aspect-square rounded-lg overflow-hidden shrink-0 transition-all ${idx === currentIndex ? "ring-2 ring-white scale-105" : "opacity-50 hover:opacity-100"
                                    }`}
                            >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
